import { environment } from '../../environments/environment';
import { Component, OnInit, OnChanges, Renderer2, Inject, ViewChild, ElementRef, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { HttpService } from '../shared/core/http.service';
import { installPasProxyAuthHeaders } from './pas-proxy-auth';
// import { DOCUMENT } from '@angular/common';

const VIEWER_ASSETS_BASE_ROUTE = '/assets/viewer-assets';
const PAS_PROXY_BASE_ROUTE = 'pas-proxy';
const API_URL = environment.apiUrl;
declare var window: any;

@Component({
  selector: 'app-prizmdoc-viewer-wrapper',
  standalone: false,
  templateUrl: './prizmdoc-viewer-wrapper.component.html',
  styleUrls: ['./prizmdoc-viewer-wrapper.component.css']
})

export class PrizmDocViewerWrapperComponent implements OnInit, OnChanges, AfterViewInit {

  // This is the one required input. Once it is set to an actual value, the
  // viewer will be instantiated with this viewingSessionId.
  @Input() viewingSessionId: string = '';

  @Input()
  width = "100%";

  @Input()
  height = "100%";

  @Output()
  viewerControl = new EventEmitter<any>();
  
  @Output()
  annotationsChanged = new EventEmitter<void>();

  prerequisiteError: Error = null;

  @ViewChild('prizmdocViewerContainer', { static: false })
  private container: ElementRef;

  private viewerCreated: boolean = false;
  private viewInitialized: boolean = false;
  private static prerequisites: { [key: string] : ResourceLoadingState } = {};

  constructor(
    private renderer2: Renderer2,
    httpService: HttpService
  ) {
    installPasProxyAuthHeaders(httpService);
  }
  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.tryCreateViewer();
  }

  async ngOnInit(): Promise<void> {
    await this.ensurePrerequisites();
    this.tryCreateViewer();
  }

  ngOnChanges(): void {
    this.tryCreateViewer();
  }

  // index.html already loads viewer-assets. Re-injecting them and awaiting onload
  // hangs on cached CSS in Angular 22, so pccViewer() never runs.
  private areViewerPrerequisitesOnWindow(): boolean {
    return !!(
      window.$ &&
      window.$.fn &&
      typeof window.$.fn.pccViewer === 'function' &&
      window.viewerCustomizations &&
      window.viewerCustomizations.languages &&
      window.viewerCustomizations.template
    );
  }

  // Ensures the viewer prerequisites have been added to the HTML head and have loaded.
  // This only happens once, the first time a viewer is instantiated.
  private async ensurePrerequisites(): Promise<void> {
    if (this.areViewerPrerequisitesOnWindow()) {
      return;
    }
    try {
      // These resources can be safely loaded in parallel.
      await Promise.all([
        this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/viewercontrol.js`),
        this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/viewerCustomizations.js`),
        this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/jquery-3.7.1.min.js`),
        this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/underscore.min.js`),
        this.ensureCssHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/css/viewer.css`),
        this.ensureCssHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/css/normalize.min.css`),
      ]);
      // These resources must be loaded last, and in this order.
      await this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/jquery.hotkeys.min.js`);
      await this.ensureScriptHasLoaded(`${VIEWER_ASSETS_BASE_ROUTE}/js/viewer.js`);
    } catch (err) {
      this.prerequisiteError = err instanceof Error ? err : new Error(String(err));
    }
  }

  private async createViewer(): Promise<void> {
    await this.ensurePrerequisites();
    if (this.prerequisiteError || !this.container || !this.container.nativeElement) {
      return;
    }
    if (!window.$ || !window.$.fn || !window.$.fn.pccViewer) {
      throw new Error('PrizmDoc viewer prerequisites loaded but pccViewer is unavailable.');
    }
    // This is where the non-Angular viewer is actually initialized, and where
    // you can customize the viewer construction options. See
    // https://help.accusoft.com/PrizmDoc/latest/HTML/external-jQuery.fn.html#~Options
    const container = window.$(this.container.nativeElement).pccViewer({
      documentID: this.viewingSessionId,
      imageHandlerUrl: `${API_URL}${PAS_PROXY_BASE_ROUTE}`,                     // Base path the viewer should use to make requests to PAS (PrizmDoc Application Services).
      viewerAssetsPath: VIEWER_ASSETS_BASE_ROUTE,                // Base path the viewer should use for static assets
      resourcePath: `${VIEWER_ASSETS_BASE_ROUTE}/img`, // Base path the viewer should use for images
      language: window.viewerCustomizations.languages['en-US'],
      template: window.viewerCustomizations.template,
      icons: window.viewerCustomizations.icons,
      annotationsMode: "LayeredAnnotations", // Use the new "LayeredAnnotations" system, which will persist annotation data as JSON (instead of the default "LegacyAnnotations" system, which uses a different XML format)
      redactionReasons: {
        enableRedactionReasonSelection: true, // Enable the UI to allow users to select a redaction reason.
        enableFreeformRedactionReasons: true, // Allow users to type a custom redaction reason.
        enableMultipleRedactionReasons: true, // Allow users to apply multiple redaction reasons to a single redaction (requires a backend running version 13.13 or higher)

        // TODO: Define your own set of redaction reasons for your users to pick from:
        reasons: [{
          reason: '1.a',                   // Text to apply to the redaction itself.
          description: 'Client Privilege'  // Optional extended description the user will see when choosing from the list of redaction reasons.
        }, {
          reason: '1.b',
          description: 'Privacy Information'
        }, {
          reason: '1.c'
        }]
      },
      uiElements: {
        attachments: true,                 // Enable the email attachments UI
        advancedSearch: true,              // Enable advanced search
      },
      immediateActionMenuMode: "hover",    // Enable immediate action menu
      attachmentViewingMode: "ThisViewer", // The email attachment will be opened in the same view
    });

    const viewerControl = container.viewerControl;
    viewerControl.on('MarkCreated', () => this.handleAnnotationChange());
    viewerControl.on('MarkRemoved', () => this.handleAnnotationChange());
    viewerControl.on('MarkChanged', () => this.handleAnnotationChange());
    viewerControl.on('MarkReordered', () => this.handleAnnotationChange());
    viewerControl.on('CommentCreated', () => this.handleAnnotationChange());
    viewerControl.on('CommentChanged', () => this.handleAnnotationChange());
    viewerControl.on('CommentRemoved', () => this.handleAnnotationChange());


    // Now that the viewer is constructed, emit the viewerControl as an output
    // so that the consumer of this component can have access to the
    // viewerControl client API.
    this.viewerControl.emit(container.viewerControl);
    this.viewerCreated = true;
  }

  private tryCreateViewer(): void {
    if (this.viewerCreated || !this.viewInitialized || !this.viewingSessionId) {
      return;
    }

    this.createViewer().catch((err) => {
      this.prerequisiteError = err instanceof Error ? err : new Error(String(err));
    });
  }

  private ensureScriptHasLoaded (src: string) {
    return this.ensureHeadResourceExistsAndHasLoaded('script', 'src', src, { async: false });
  }

  private ensureCssHasLoaded (href: string) {
    return this.ensureHeadResourceExistsAndHasLoaded('link', 'href', href, { rel: 'stylesheet' });
  }

  private isResourceAlreadyInDocument(tagName: string, urlValue: string): boolean {
    const relativeUrl = urlValue.replace(/^\//, '');
    const nodes = tagName === 'script'
      ? Array.from(document.getElementsByTagName('script'))
      : Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    return nodes.some((node: Element) => {
      const current = node.getAttribute(tagName === 'script' ? 'src' : 'href') || '';
      return current === urlValue
        || current === relativeUrl
        || current.endsWith('/' + relativeUrl)
        || current.endsWith(relativeUrl);
    });
  }

  private ensureHeadResourceExistsAndHasLoaded (tagName: string, urlPropertyName: string, urlValue: string, attributes: Object) {
    return new Promise<void>((resolve, reject) => {
      if (!PrizmDocViewerWrapperComponent.prerequisites[urlValue]) {
        PrizmDocViewerWrapperComponent.prerequisites[urlValue] = new ResourceLoadingState();

        if (this.isResourceAlreadyInDocument(tagName, urlValue)) {
          PrizmDocViewerWrapperComponent.prerequisites[urlValue].setLoaded();
        } else {
          const tag = this.renderer2.createElement(tagName);

          tag.onload = () => {
            tag.onerror = null;
            tag.onload = null;
            PrizmDocViewerWrapperComponent.prerequisites[urlValue].setLoaded();
          };

          tag.onerror = (err: Error) => {
            tag.onerror = null;
            tag.onload = null;
            PrizmDocViewerWrapperComponent.prerequisites[urlValue].setErrored(err);
          };

          for (const [k, v] of Object.entries(attributes || {})) {
            tag.setAttribute(k, v);
          }

          document.head.appendChild(tag);
          tag[urlPropertyName] = urlValue;
        }
      }

      if (PrizmDocViewerWrapperComponent.prerequisites[urlValue].isLoaded()) {
        resolve();
      } else if (PrizmDocViewerWrapperComponent.prerequisites[urlValue].isErrored()) {
        reject(new Error(`Failed to load ${urlValue}`));
      } else {
        PrizmDocViewerWrapperComponent.prerequisites[urlValue].onLoad(() => resolve());
        PrizmDocViewerWrapperComponent.prerequisites[urlValue].onError(() => reject(new Error(`Failed to load ${urlValue}`)));
      }
    });
  }

  private handleAnnotationChange(): void {
    this.annotationsChanged.emit();
  }
}

class ResourceLoadingState {
  private _isLoaded: boolean = false;
  private _isErrored: boolean = false;
  private _loadedCallbacks: (() => any)[] = [];
  private _errorCallbacks: ((err: Error) => any)[] = [];

  onLoad(f: () => any) {
    this._loadedCallbacks.push(f);
  }

  onError(f: (err: Error) => any) {
    this._errorCallbacks.push(f);
  }

  setLoaded() {
    this._isLoaded = true;
    this._loadedCallbacks.forEach(f => f());
  }

  setErrored(err: Error) {
    this._isErrored = true;
    this._errorCallbacks.forEach(f => f(err));
  }

  isLoaded() {
    return this._isLoaded;
  }

  isErrored() {
    return this._isErrored;
  }

  
}
